import React, { useEffect, useState } from 'react';
import {
  ContentQualityQuestionData,
  ContentQualityResponseOptions,
  ContentType,
  QuestionResponse,
  SequencedQuestionResponse,
  WorkflowEvent,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { withAppContext } from '../../contexts/AppContext';
import { Box, Grid, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-core';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import Breadcrumbs from '../common/Breadcrumbs';
import ContentQualityQuestionnaireForm from './ContentQualityQuestionnaireForm';
import ContentQualityQuestionnaireActionBox from './ContentQualityQuestionnaireActionBox';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  calculateQualityScore,
  CategoryOrder,
  createSortedCategories,
  populateDependentMap,
  populateQuestionMaps,
  responsesArrayToMap,
  responsesMapToArray,
  updateDependentResponses,
  updateVisibilityMap,
} from '../utils/ContentQualityUtils';
import { withRouter } from 'react-router';
import AppContextProps from '../../contexts/AppContextProps';
import { ContentQualityQuestionnaireState } from '../../common/ContentQualityQuestionnaireState';
import LocalStorage from '../../common/LocalStorage';
import { convertEntities } from '@amzn/awscat-editor';
import { isUnpublishRecommended } from '../../api/ContentUtil';

interface Props extends AppContextProps {
  match: any;
}

let mplServiceClient: IMplServiceClient;

const ContentQualityQuestionnairePage: React.FunctionComponent<Props> = (props: Props) => {
  const contentId = decodeURIComponent(props.match.params.contentId);

  /* States */
  const [questions, setQuestions] = useState<ContentQualityQuestionData[]>([]); // static
  // content
  const [contentName, setContentName] = useState<string>('');
  const [contentType, setContentType] = useState<ContentType>();
  const [workflowId, setWorkflowId] = useState<number>(0);
  const [draftBarRaiserId, setDraftBarRaiserId] = useState<string | undefined>(undefined);
  const [contentMainTechDomain, setContentMainTechDomain] = useState<string>();
  // for dependency
  const [idToSequenceMap, setIdToSequenceMap] = useState<Map<string, number>>(
    new Map<string, number>(),
  ); // static
  const [sequenceToDependentMap, setSequenceToDependentMap] = useState<Map<number, number[]>>(
    new Map<number, number[]>(),
  ); // sequence -> questions that depend on it. static
  const [sequenceToVisibilityMap, setSequenceToVisibilityMap] = useState<Map<number, boolean>>(
    new Map<number, boolean>(),
  ); // dynamic
  // to display each question
  const [sequenceToQuestionMap, setSequenceToQuestionMap] = useState<
    Map<number, ContentQualityQuestionData>
  >(new Map<number, ContentQualityQuestionData>()); // static
  // to display questions in a category
  const [categoryToSequencesMap, setCategoryToSequencesMap] = useState<Map<string, number[]>>(
    new Map<string, number[]>(),
  ); // static
  // to sort categories
  const [categories, setCategories] = useState<CategoryOrder[]>([]); // static
  // for response
  const [sequenceToResponseMap, setSequenceToResponseMap] = useState<Map<number, QuestionResponse>>(
    new Map<number, QuestionResponse>(),
  );
  const [score, setScore] = useState<number>(-1);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isUnreviewedPublishedContent, setIsUnreviewedPublishedContent] = useState<boolean>(false);
  const [lastOverallComment, setLastOverallComment] = useState<string>('');
  const [unpublishRecommended, setUnpublishRecommended] = useState<boolean>(false);

  /* Initialize */
  useEffect(() => {
    async function loadAllQuestions() {
      setIsLoading(true);

      mplServiceClient = MplServiceClientFactory.create();

      // get content related info
      const draftContent = await mplServiceClient.getContent(contentId);
      const { content, workflow } = draftContent;
      const draftContentType = content.contentMetadata.contentType;
      const wfId = workflow.draftWorkflowId;
      const mainTechDomain = content.templateMetadata.mainTechDomain;

      const lastQualityEvaluation =
        await mplServiceClient.getLastContentQualityEvaluation(contentId);
      const isUnreviewedPublishedContent =
        draftContent.workflow.draftState === WorkflowState.UNMODIFIED &&
        lastQualityEvaluation.responses.length === 0 &&
        !lastQualityEvaluation.score;
      setIsUnreviewedPublishedContent(isUnreviewedPublishedContent);

      const questionIdToSequenceMap = new Map<string, number>();
      const questionSequenceToQuestionMap = new Map<number, ContentQualityQuestionData>();
      const questionCategoryToSequencesMap = new Map<string, number[]>();
      const questionCategories: CategoryOrder[] = [];
      let questionSequenceToResponseMap = new Map<number, QuestionResponse>();

      const questionnaireState = loadQuestionnaireState(wfId);

      let questionnaireQuestions: ContentQualityQuestionData[];

      if (questionnaireState && questionnaireState.questions) {
        // load questions, responses and errors from local storage
        questionnaireQuestions = questionnaireState.questions;
        questionSequenceToResponseMap = responsesArrayToMap(questionnaireState.responses);
      } else {
        questionnaireQuestions = (
          await mplServiceClient.getQuestionnaireQuestions(draftContentType)
        ).questions;
      }

      // load last quality response to read only mode
      if (lastQualityEvaluation.responses.length > 0 && isUnpublishRecommended(workflow)) {
        // convert last quality eval to map
        const lastResponses = lastQualityEvaluation.responses.map((response) => {
          return {
            sequence: Number(response.questionSequence),
            response: {
              response: response.response,
              comment: response.comment,
            } as QuestionResponse,
          } as SequencedQuestionResponse;
        });
        // populate responses from prev bar raiser
        questionSequenceToResponseMap = responsesArrayToMap(lastResponses);
        setScore(lastQualityEvaluation.score);
        setUnpublishRecommended(true);
      }

      // get unpublish recommend comment
      if (workflow.publicationWorkflowId) {
        const publicationEvents = await mplServiceClient.getContentEvents(
          contentId,
          workflow.publicationWorkflowId,
        );
        if (publicationEvents) {
          const wfEvents: WorkflowEvent[] = publicationEvents.events
            // @ts-expect-error TS7006 - strict migration
            .sort((a, b) => b.createdAt - a.createdAt)
            .filter((wfEv) => wfEv.type === WorkflowEventTypes.UNPUBLISH_RECOMMENDED);
          if (wfEvents.length > 0 && isUnpublishRecommended(workflow)) {
            const unpublishEvent = wfEvents[0];
            // @ts-expect-error TS18048 - strict migration
            setLastOverallComment(unpublishEvent.comment);
          }
        }
      }

      // questions and categories
      populateQuestionMaps(
        questionnaireQuestions,
        questionIdToSequenceMap,
        questionSequenceToQuestionMap,
        questionCategoryToSequencesMap,
      );
      createSortedCategories(questionCategoryToSequencesMap, questionCategories);

      // manage dependency / visibility of questions
      const dependentMap = new Map<number, number[]>(); // sequence -> sequences of questions that depend on it
      const visibilityMap = new Map<number, boolean>();
      populateDependentMap(questionIdToSequenceMap, questionSequenceToQuestionMap, dependentMap);
      updateVisibilityMap(
        questionIdToSequenceMap,
        questionSequenceToQuestionMap,
        questionSequenceToResponseMap,
        visibilityMap,
      );

      // score
      const currentScore = calculateQualityScore(questionSequenceToResponseMap, visibilityMap);

      // content
      setContentName(
        content.contentMetadata.contentShortName || content.contentMetadata.contentName,
      );
      setContentType(draftContentType);
      setWorkflowId(wfId);
      setDraftBarRaiserId(workflow.barRaiserId);
      setContentMainTechDomain(mainTechDomain);

      // categories and questions
      setQuestions(questionnaireQuestions);
      setCategories(questionCategories);
      setCategoryToSequencesMap(questionCategoryToSequencesMap);
      setSequenceToQuestionMap(questionSequenceToQuestionMap);

      // dependency
      setIdToSequenceMap(questionIdToSequenceMap);
      setSequenceToVisibilityMap(visibilityMap);
      setSequenceToDependentMap(dependentMap);

      // responses, errors and score loaded from local storage
      setSequenceToResponseMap(questionSequenceToResponseMap);
      setScore(currentScore);

      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });

      setIsLoading(false);
    }

    loadAllQuestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQuestionnaireState = (wfId: number) => {
    const questionnaireState: ContentQualityQuestionnaireState =
      LocalStorage.getContentQualityQuestionnaireState(contentId, wfId);
    return questionnaireState;
  };

  const saveQuestionnaireState = (
    wfId: number,
    sequenceToResponses: Map<number, QuestionResponse>,
  ) => {
    const questionnaireState = ContentQualityQuestionnaireState.createNew(
      questions,
      responsesMapToArray(sequenceToResponses),
    );
    LocalStorage.setContentQualityQuestionnaireState(contentId, wfId, questionnaireState);
  };

  const resetQuestionnaireState = (wfId: number) => {
    LocalStorage.resetContentQualityQuestionnaireState(contentId, wfId);
  };

  const onReviewCompleted = () => {
    resetQuestionnaireState(workflowId);
  };

  const breadcrumbItems = [
    { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
    {
      text: convertEntities(contentName, props.appContext.entities),
      href: getContentPath({ contentId, mode: 'draft', role: 'bar-raiser' }),
    },
    { text: constants.BAR_RAISER_QUESTIONNAIRE, href: '' },
  ];

  const header = (
    <Header variant="h2">
      {constants.BAR_RAISER_QUESTIONNAIRE} - {contentType}
    </Header>
  );

  const feedbackInfoMessage = (
    <div className="amazon-orange-color-botton-hover">
      {constants.PROVIDE_SUMMARIZED_FEEDBACK}
      <br />
      {constants.PROVIDE_DETAILED_FEEDBACK}
    </div>
  );

  const onResponseMapUpdated = (
    sequence: number,
    response: ContentQualityResponseOptions,
    comment: string,
  ) => {
    // response
    const updatedQuestionResponse: QuestionResponse = { response, comment };
    const updatedSequenceToResponseMap = new Map(sequenceToResponseMap);
    updatedSequenceToResponseMap.set(sequence, updatedQuestionResponse);

    // dependency
    const updatedVisibilityMap = new Map(sequenceToVisibilityMap);
    const visibilityMapShouldUpdate = updateDependentResponses(
      sequence,
      response,
      sequenceToDependentMap,
      updatedSequenceToResponseMap,
    );
    if (visibilityMapShouldUpdate) {
      updateVisibilityMap(
        idToSequenceMap,
        sequenceToQuestionMap,
        updatedSequenceToResponseMap,
        updatedVisibilityMap,
      );
    }

    // score
    const updatedScore = calculateQualityScore(updatedSequenceToResponseMap, updatedVisibilityMap);

    setSequenceToResponseMap(updatedSequenceToResponseMap);
    setSequenceToVisibilityMap(updatedVisibilityMap);
    setScore(updatedScore);
    saveQuestionnaireState(workflowId, updatedSequenceToResponseMap);
  };

  const body = isLoading ? (
    <Spinner size="normal" />
  ) : (
    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 4 }]}>
      <ContentQualityQuestionnaireForm
        categories={categories.map((cat) => cat.category)}
        categoryToSequencesMap={categoryToSequencesMap}
        sequenceToQuestionMap={sequenceToQuestionMap}
        sequenceToVisibilityMap={sequenceToVisibilityMap}
        sequenceToResponseMap={sequenceToResponseMap}
        handleResponseUpdated={onResponseMapUpdated}
        // @ts-expect-error TS2322 - strict migration
        contentType={contentType}
      />
      <ContentQualityQuestionnaireActionBox
        contentId={contentId}
        workflowId={workflowId || 0}
        draftBarRaiserId={draftBarRaiserId}
        idToSequenceMap={idToSequenceMap}
        sequenceToResponseMap={sequenceToResponseMap}
        score={score}
        handleReviewComplete={onReviewCompleted}
        // @ts-expect-error TS2322 - strict migration
        contentMainTechDomain={contentMainTechDomain}
        isUnreviewedPublishedContent={isUnreviewedPublishedContent}
        lastOverallComment={lastOverallComment}
        unpublishRecommended={unpublishRecommended}
      />
    </Grid>
  );

  return (
    <div id="bar-raiser-questionnaire">
      <Box margin={{ left: 'xl', top: 's', bottom: 'xl', right: 'xl' }}>
        <Breadcrumbs items={breadcrumbItems} />
        <Box margin={{ left: 'l', top: 'm', bottom: 'l', right: 'l' }}>
          <SpaceBetween direction="vertical" size="xl">
            {header}
            {feedbackInfoMessage}
            {body}
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );
};

export default withRouter(withAppContext(ContentQualityQuestionnairePage));
